// Workspace — one home for the feedback loop. Collapses what used to be three
// sidebar items (Testing, Tickets, Board) into a single menu with in-page tabs.
//
// Each tab IS its own route (/testing, /tickets, /board) — clicking a tab is a
// real navigation, so deep links and the back button work, the active tab can
// never fall out of sync with the URL, and there's no query-param tab state to
// special-case. The route's `initialTab` selects which panel renders.

import { Link } from 'react-router-dom';
import { FlaskConical, Ticket, KanbanSquare } from 'lucide-react';
import Testing from './Testing';
import Tickets from './Tickets';
import Board from './Board';

type TabId = 'submit' | 'tickets' | 'board';

const TABS: { id: TabId; label: string; icon: typeof FlaskConical; path: string }[] = [
  { id: 'submit', label: 'Submit feedback', icon: FlaskConical, path: '/testing' },
  { id: 'tickets', label: 'Tickets', icon: Ticket, path: '/tickets' },
  { id: 'board', label: 'Board', icon: KanbanSquare, path: '/board' },
];

const SUBTITLE: Record<TabId, string> = {
  submit: "Tell us what's working, what isn't, and what you'd like next — it becomes a ticket.",
  tickets: 'Every piece of feedback and every request, in one place.',
  board: 'Drag a ticket across the pipeline as the team works it.',
};

export default function Workspace({ initialTab = 'submit' }: { initialTab?: TabId }) {
  const tab = initialTab;

  return (
    <div className="max-w-[1400px] mx-auto">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-foreground tracking-tight">
          Testing &amp; Feedback
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{SUBTITLE[tab]}</p>
      </header>

      {/* Tab bar — each tab is a route link */}
      <div className="inline-flex items-center gap-1 rounded-xl neo-inset p-1 mb-5" role="tablist">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <Link
              key={t.id}
              to={t.path}
              role="tab"
              aria-selected={active}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                active
                  ? 'bg-card text-forest shadow-sm'
                  : 'text-foreground/65 hover:text-foreground'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </Link>
          );
        })}
      </div>

      {tab === 'submit' && <Testing />}
      {tab === 'tickets' && <Tickets />}
      {tab === 'board' && <Board />}
    </div>
  );
}
